// Hierarchies.tsx — taxonomy tree management with live API.

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { Hierarchy, HierarchyNode } from "@/lib/types";

type TNode = HierarchyNode & { children?: TNode[] };

function findNode(roots: TNode[], id: string): TNode | null {
  for (const n of roots) {
    if (n.id === id) return n;
    if (n.children) { const r = findNode(n.children, id); if (r) return r; }
  }
  return null;
}
function findPath(roots: TNode[], id: string, acc: TNode[] = []): TNode[] | null {
  for (const n of roots) {
    if (n.id === id) return [...acc, n];
    if (n.children) {
      const r = findPath(n.children, id, [...acc, n]);
      if (r) return r;
    }
  }
  return null;
}
function flatten(roots: TNode[], expanded: Set<string>): Array<{ node: TNode; depth: number; parents: string[] }> {
  const out: Array<{ node: TNode; depth: number; parents: string[] }> = [];
  function walk(nodes: TNode[], depth: number, parents: string[]) {
    for (const n of nodes) {
      out.push({ node: n, depth, parents });
      if (n.children && expanded.has(n.id)) walk(n.children, depth + 1, [...parents, n.id]);
    }
  }
  walk(roots, 0, []);
  return out;
}
function isDescendant(node: TNode, id: string): boolean {
  if (node.id === id) return true;
  return !!node.children?.some(c => isDescendant(c, id));
}

export function Hierarchies() {
  const { hierarchyId } = useParams();
  const navigate = useNavigate();
  const [hierarchies, setHierarchies] = useState<Hierarchy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(hierarchyId ?? null);
  const active = hierarchies.find(h => h.id === activeId) ?? hierarchies[0] ?? null;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string>("");
  const [renameDraft, setRenameDraft] = useState<string>("");
  const [q, setQ] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const toast = useToast();

  async function reloadAll() {
    setLoading(true); setError(null);
    try {
      const hs = await api.hierarchies.list();
      setHierarchies(hs as Hierarchy[]);
      if (!activeId && hs[0]) setActiveId(hs[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }
  async function reloadActive() {
    if (!active) return;
    try {
      const h = await api.hierarchies.get(active.id);
      setHierarchies(hs => hs.map(x => x.id === h.id ? h : x));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Refresh failed", "error");
    }
  }

  useEffect(() => { reloadAll(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (active) {
      const sel = findNode(active.rootNodes as TNode[], selectedId);
      if (!sel && active.rootNodes[0]) setSelectedId(active.rootNodes[0].id);
      const found = sel ?? (active.rootNodes[0] as TNode | undefined);
      if (found) setRenameDraft(found.label);
    }
  }, [active, selectedId]);

  function switchHierarchy(id: string) {
    setActiveId(id);
    navigate(`/hierarchies/${id}`, { replace: true });
  }
  function toggle(id: string) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  }

  async function reparent(nodeId: string, newParentId: string | null) {
    if (!active || nodeId === newParentId) return;
    const node = findNode(active.rootNodes as TNode[], nodeId);
    if (!node) return;
    if (newParentId && isDescendant(node, newParentId)) { toast("Can't drop into own descendant", "warn"); return; }
    try {
      await api.hierarchies.reparent(active.id, nodeId, newParentId);
      toast(`Moved "${node.label}"`, "ok");
      if (newParentId) setExpanded(new Set([...expanded, newParentId]));
      await reloadActive();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Move failed", "error");
    }
  }

  async function addChild(parentId: string | null) {
    if (!active) return;
    try {
      const created = await api.hierarchies.addNode(active.id, { label: "New node", parentId: parentId ?? undefined });
      toast("Child added", "ok");
      if (parentId) setExpanded(new Set([...expanded, parentId]));
      await reloadActive();
      setSelectedId(created.id);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Add failed", "error");
    }
  }

  async function removeSelected() {
    if (!active || !selectedId) return;
    const node = findNode(active.rootNodes as TNode[], selectedId);
    if (!node) return;
    if (!confirm(`Delete "${node.label}" and all descendants?`)) return;
    try {
      await api.hierarchies.removeNode(active.id, selectedId);
      toast(`Deleted "${node.label}"`, "warn");
      setSelectedId("");
      await reloadActive();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Delete failed", "error");
    }
  }

  async function commitRename() {
    if (!active || !selectedId) return;
    const node = findNode(active.rootNodes as TNode[], selectedId);
    if (!node || renameDraft === node.label) return;
    try {
      await api.hierarchies.renameNode(active.id, selectedId, renameDraft);
      toast("Rename saved", "ok");
      await reloadActive();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Rename failed", "error");
    }
  }

  if (loading) return <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>Loading hierarchies…</div>;
  if (error) return <div style={{ padding: 24, color: "var(--md-error)" }}>{error}</div>;
  if (!active) return <div style={{ padding: 24 }}>No hierarchies yet.</div>;

  const rootNodes = active.rootNodes as TNode[];
  const flat = flatten(rootNodes, expanded);
  const flatFiltered = q ? flat.filter(f => f.node.label.toLowerCase().includes(q.toLowerCase())) : flat;
  const selectedNode = findNode(rootNodes, selectedId);
  const pathNodes = findPath(rootNodes, selectedId) ?? [];

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "260px minmax(320px, 1.2fr) minmax(280px, 1fr)",
      height: "calc(100vh - 60px)",
      background: "var(--md-bg)",
    }}>
      <aside style={{ borderRight: "1px solid var(--md-outline-var)", overflowY: "auto" }}>
        <div style={{ padding: "20px 20px 12px" }}>
          <div className="font-mono" style={eyebrowStyle()}>HIERARCHIES</div>
          <h2 className="font-display" style={{ margin: "6px 0 4px", font: "var(--t-headline-sm)" }}>Taxonomies</h2>
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {hierarchies.map(h => {
            const sel = h.id === active.id;
            return (
              <li key={h.id}>
                <button onClick={() => switchHierarchy(h.id)} className="row-hover" style={{
                  width: "100%", textAlign: "left", appearance: "none", border: "none",
                  background: sel ? "var(--md-secondary-container)" : "transparent",
                  color: sel ? "var(--md-on-secondary-container)" : "var(--md-on-surface)",
                  padding: "12px 20px", display: "grid", gridTemplateColumns: "1fr auto", gap: 8, cursor: "pointer",
                }}>
                  <div>
                    <div style={{ font: "var(--t-title-sm)" }}>{h.name}</div>
                    <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>{h.domainId}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <section aria-label={`Tree view: ${active.name}`}
        style={{ borderRight: "1px solid var(--md-outline-var)", display: "flex", flexDirection: "column", minWidth: 0 }}
      >
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid var(--md-outline-var)" }}>
          <div className="font-mono" style={eyebrowStyle()}>{active.domainId.toUpperCase()} · TREE</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h1 className="font-display" style={{
              margin: "6px 0 12px", font: "var(--t-headline-md)", flex: 1,
              fontStyle: "italic", fontWeight: 400,
            }}>{active.name}</h1>
            <button onClick={() => addChild(null)} style={btnOutlined()}>+ Root node</button>
          </div>
          <input
            type="search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search within tree…" aria-label="Search tree"
            style={{
              width: "100%", boxSizing: "border-box", height: 38,
              padding: "0 14px", background: "var(--md-sc-low)",
              border: "1px solid var(--md-outline-var)",
              borderRadius: "var(--r-pill)", font: "var(--t-body-md)", outline: "none",
            }}
          />
        </div>
        <div className="scroll" role="tree" tabIndex={0} aria-label="Hierarchy"
          style={{ flex: 1, overflowY: "auto", padding: "8px 0", outline: "none" }}
        >
          {flatFiltered.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: "var(--md-on-surface-var)" }}>
              <div style={{ font: "var(--t-title-sm)", marginTop: 6 }}>No nodes</div>
            </div>
          )}
          {flatFiltered.map(({ node, depth, parents }) => (
            <TreeRow
              key={node.id}
              node={node as TNode}
              depth={depth}
              parents={parents}
              expanded={expanded.has(node.id)}
              selected={selectedId === node.id}
              dragging={dragId === node.id}
              dropOver={dropTarget === node.id}
              hasChildren={!!(node as TNode).children?.length}
              onToggle={() => toggle(node.id)}
              onSelect={() => { setSelectedId(node.id); setRenameDraft(node.label); }}
              onDragStart={() => setDragId(node.id)}
              onDragEnd={() => { setDragId(null); setDropTarget(null); }}
              onDragOver={() => setDropTarget(node.id)}
              onDrop={() => {
                if (dragId) reparent(dragId, node.id);
                setDragId(null); setDropTarget(null);
              }}
              onAddChild={() => addChild(node.id)}
            />
          ))}
        </div>
      </section>

      <aside className="scroll" style={{ overflowY: "auto", padding: "24px 28px" }}>
        {selectedNode ? (
          <>
            <div className="font-mono" style={{ ...eyebrowStyle(), color: "var(--md-on-surface-var)", display: "flex", flexWrap: "wrap", gap: 4 }}>
              {pathNodes.map((p, i) => (
                <span key={p.id}>
                  {i > 0 && " / "}
                  <span style={{ color: p.id === selectedNode.id ? "var(--md-on-surface)" : undefined }}>{p.label}</span>
                </span>
              ))}
            </div>
            <input
              value={renameDraft}
              onChange={e => setRenameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              aria-label="Node label"
              className="font-display"
              style={{
                margin: "10px 0 16px", padding: "4px 0",
                font: "var(--t-headline-md)", letterSpacing: "-0.02em",
                fontStyle: "italic", fontWeight: 400,
                width: "100%", background: "transparent", border: "none",
                borderBottom: "2px solid transparent", outline: "none",
                color: "var(--md-on-surface)",
              }}
              onFocus={e => (e.currentTarget.style.borderBottomColor = "var(--md-primary)")}
            />
            <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
              <Pill tone="secondary">
                <span className="font-mono">{(selectedNode.recordCount ?? 0).toLocaleString()}</span>&nbsp;records
              </Pill>
              <Pill tone="neutral">
                <span className="font-mono">{(selectedNode as TNode).children?.length ?? 0}</span>&nbsp;children
              </Pill>
              {selectedNode.badge && <Pill tone="tertiary">{selectedNode.badge}</Pill>}
            </div>

            <SectionEyebrow>Path</SectionEyebrow>
            <div style={{
              marginTop: 8, padding: "10px 14px",
              background: "var(--md-sc-low)", borderRadius: "var(--r-sm)",
              border: "1px solid var(--md-outline-var)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <code className="font-mono" style={{ font: "var(--t-mono-md)", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                {pathNodes.map(p => p.id).join(" / ")}
              </code>
              <button
                onClick={() => navigator.clipboard?.writeText(pathNodes.map(p => p.id).join("/"))}
                aria-label="Copy path"
                style={{
                  appearance: "none", border: "none", cursor: "pointer",
                  background: "transparent", color: "var(--md-on-surface-var)",
                  font: "var(--t-label-md)", padding: "4px 8px", borderRadius: "var(--r-sm)",
                }}
              >Copy</button>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 24, flexWrap: "wrap" }}>
              <button style={btnFilled()} onClick={() => addChild(selectedNode.id)}>Add child</button>
              <button style={btnOutlined()} onClick={removeSelected}>Delete</button>
            </div>
          </>
        ) : (
          <div style={{ color: "var(--md-on-surface-var)" }}>Select a node to see details.</div>
        )}
      </aside>
    </div>
  );
}

function TreeRow({ node, depth, expanded, selected, dragging, dropOver, hasChildren, onToggle, onSelect, onDragStart, onDragEnd, onDragOver, onDrop, onAddChild }: {
  node: TNode; depth: number; parents: string[];
  expanded: boolean; selected: boolean; dragging: boolean; dropOver: boolean; hasChildren: boolean;
  onToggle: () => void; onSelect: () => void;
  onDragStart: () => void; onDragEnd: () => void; onDragOver: () => void; onDrop: () => void;
  onAddChild: () => void;
}) {
  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? expanded : undefined}
      aria-selected={selected}
      aria-level={depth + 1}
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(); }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDragOver(); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      onClick={onSelect}
      onDoubleClick={onToggle}
      className="row-hover"
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: "20px 1fr auto auto",
        gap: 10, alignItems: "center",
        padding: `8px 16px 8px ${16 + depth * 20}px`,
        cursor: "pointer",
        background: selected ? "var(--md-secondary-container)"
          : dropOver ? "var(--md-primary-container)" : "transparent",
        color: selected ? "var(--md-on-secondary-container)" : "var(--md-on-surface)",
        opacity: dragging ? 0.4 : 1,
        borderRadius: selected ? "var(--r-sm)" : 0,
        margin: selected ? "0 8px" : 0,
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(); }}
        aria-label={hasChildren ? (expanded ? "Collapse" : "Expand") : "Leaf node"}
        aria-hidden={!hasChildren}
        tabIndex={hasChildren ? 0 : -1}
        style={{
          width: 20, height: 20, display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          appearance: "none", border: "none", background: "transparent",
          cursor: hasChildren ? "pointer" : "default",
          color: "var(--md-on-surface-var)",
          transform: hasChildren && expanded ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform var(--d-short-2) var(--ease-emphasized)",
        }}
      >
        {hasChildren ? "▸" : <span style={{ width: 4, height: 4, borderRadius: 999, background: "var(--md-outline)" }} />}
      </button>
      <span style={{
        font: depth === 0 ? "var(--t-title-sm)" : "var(--t-body-md)",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{node.label}</span>
      {node.badge && <Pill tone="tertiary">{node.badge}</Pill>}
      <span className="font-mono tabular" style={{
        font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)",
      }}>{(node.recordCount ?? 0).toLocaleString()}</span>

      <button
        onClick={(e) => { e.stopPropagation(); onAddChild(); }}
        aria-label={`Add child to ${node.label}`}
        title="Add child"
        style={{
          position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
          width: 24, height: 24, borderRadius: 999, border: "1px solid var(--md-outline-var)",
          background: "var(--md-sc-lowest)", color: "var(--md-on-surface-var)",
          cursor: "pointer", display: "none",
        }}
      >+</button>
    </div>
  );
}

function Pill({ children, tone }: { children: ReactNode; tone: "primary" | "secondary" | "tertiary" | "neutral" }) {
  const map = {
    primary:   { bg: "var(--md-primary-container)",   fg: "var(--md-on-primary-container)" },
    secondary: { bg: "var(--md-secondary-container)", fg: "var(--md-on-secondary-container)" },
    tertiary:  { bg: "var(--md-tertiary-container)",  fg: "var(--md-on-tertiary-container)" },
    neutral:   { bg: "var(--md-sc-low)",              fg: "var(--md-on-surface-var)" },
  } as const;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: "var(--r-pill)",
      background: map[tone].bg, color: map[tone].fg,
      font: "var(--t-label-sm)", letterSpacing: "0.02em",
    }}>{children}</span>
  );
}

function SectionEyebrow({ children }: { children: ReactNode }) {
  return <div className="font-mono" style={{
    font: "var(--t-label-md)", color: "var(--md-on-surface-var)",
    textTransform: "uppercase", letterSpacing: "0.12em",
  }}>{children}</div>;
}
function eyebrowStyle(): CSSProperties {
  return {
    font: "var(--t-mono-sm)", color: "var(--md-tertiary)",
    textTransform: "uppercase", letterSpacing: "0.12em",
  };
}
function btnFilled(): CSSProperties {
  return {
    appearance: "none", border: "none", cursor: "pointer",
    background: "var(--md-primary)", color: "var(--md-on-primary)",
    font: "var(--t-label-lg)", height: 38, padding: "0 18px",
    borderRadius: "var(--r-pill)",
  };
}
function btnOutlined(): CSSProperties {
  return {
    appearance: "none", cursor: "pointer",
    background: "transparent", color: "var(--md-on-surface)",
    border: "1px solid var(--md-outline)",
    font: "var(--t-label-lg)", height: 38, padding: "0 18px",
    borderRadius: "var(--r-pill)",
  };
}

export default Hierarchies;
